/**
 * Persistent tower Research — ranks are a separate axis from match towerLevel (LV1–LV4).
 */

export type ResearchTowerId = 'fire' | 'frost' | 'cannon' | 'poison' | 'aa' | 'mage'
export type ResearchBranchId = 'power' | 'range' | 'tempo'

export const RESEARCH_TOWER_IDS: readonly ResearchTowerId[] = [
  'fire',
  'frost',
  'cannon',
  'poison',
  'aa',
  'mage'
] as const

export const RESEARCH_BRANCH_IDS: readonly ResearchBranchId[] = [
  'power',
  'range',
  'tempo'
] as const

export const RESEARCH_MAX_RANK: Record<ResearchBranchId, number> = {
  power: 8,
  range: 5,
  tempo: 8
}

/** Ranks beyond this are Advanced Research (unlocked by Chapter 2 entry). */
export const RESEARCH_BASE_MAX_RANK: Record<ResearchBranchId, number> = {
  power: 5,
  range: 3,
  tempo: 5
}

/** Per-tower branch ranks (0 = unresearched). */
export interface TowerResearchRanks {
  power: number
  range: number
  tempo: number
}

export interface ResearchState {
  version: 1
  towers: Record<ResearchTowerId, TowerResearchRanks>
}

/**
 * Runtime modifiers applied by resolveTowerStats.
 * Research Rank is NOT towerLevel.
 */
export interface ResearchModifiers {
  damageMultiplier: number
  rangeMultiplier: number
  attackIntervalMultiplier: number
  splashRadiusMultiplier: number
  /** Kept at 1.0 for M51–M54 — durations not scaled by Research. */
  statusDurationMultiplier: number
  /** Frost POWER: additive slow bonus (clamped in resolver). */
  frostSlowBonus: number
  /** Poison POWER: DoT DPS multiplier. */
  poisonDotMultiplier: number
  /** Mage POWER: Armor Break magnitude multiplier (clamped in resolver). */
  armorBreakMagnitudeMultiplier: number
}

export const IDENTITY_RESEARCH: ResearchModifiers = {
  damageMultiplier: 1,
  rangeMultiplier: 1,
  attackIntervalMultiplier: 1,
  splashRadiusMultiplier: 1,
  statusDurationMultiplier: 1,
  frostSlowBonus: 0,
  poisonDotMultiplier: 1,
  armorBreakMagnitudeMultiplier: 1
}

export function emptyTowerRanks(): TowerResearchRanks {
  return { power: 0, range: 0, tempo: 0 }
}

export function emptyResearchState(): ResearchState {
  return {
    version: 1,
    towers: {
      fire: emptyTowerRanks(),
      frost: emptyTowerRanks(),
      cannon: emptyTowerRanks(),
      poison: emptyTowerRanks(),
      aa: emptyTowerRanks(),
      mage: emptyTowerRanks()
    }
  }
}

export function isResearchTowerId(value: string): value is ResearchTowerId {
  return (RESEARCH_TOWER_IDS as readonly string[]).includes(value)
}

export function isResearchBranchId(value: string): value is ResearchBranchId {
  return (RESEARCH_BRANCH_IDS as readonly string[]).includes(value)
}
